/**
 * Endpoints `/api/v1/notifications/` — Console Admin.
 *
 * Wrappers axios typés ; la couche réseau seulement, le cache est géré par
 * TanStack Query au point d'appel. Les serializers DRF exposent les champs
 * `id`, `type`, `titre`, `message`, `lu`, `lien`, `created_at`.
 */

import { apiClient } from './client';
import type { Paginated } from './types';

// ── Types ────────────────────────────────────────────────────

/** Catégorie de notification (libre côté backend). */
export type NotificationType = string;

/** Notification telle que renvoyée par le serializer DRF. */
export interface Notification {
  id: string;
  type: NotificationType;
  titre: string;
  message: string;
  lu: boolean;
  lien: string | null;
  created_at: string;
}

// ── Fonctions API ────────────────────────────────────────────

/**
 * Liste les notifications de l'utilisateur connecté.
 * L'API peut renvoyer une liste plate ou une réponse paginée ; les deux cas
 * sont normalisés en tableau.
 */
export async function listNotifications(): Promise<Notification[]> {
  const { data } = await apiClient.get<Notification[] | Paginated<Notification>>(
    '/notifications/',
  );
  return Array.isArray(data) ? data : data.results;
}

/**
 * Nombre de notifications non lues (badge de la cloche dans l'en-tête).
 * L'endpoint `GET /notifications/non_lues/` renvoie `{ count: number }`.
 */
export async function compteNonLues(): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>('/notifications/non_lues/');
  return data.count;
}

/**
 * Marque une notification comme lue.
 * `POST /notifications/{id}/lire/`
 */
export async function marquerLue(id: string): Promise<void> {
  await apiClient.post(`/notifications/${id}/lire/`);
}
